import { Link } from "react-router-dom";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useT, useLang } from "../i18n/useT";
import { useStore } from "../store/store";
import { useReveal, useScrollProgress } from "../lib/useReveal";
import { PulseGlyph } from "../components/Logo";
import ContactForm from "../components/ContactForm";

/* ----------------------------- imagery ------------------------------ */
// Unsplash hot-linkable stock photos. Each URL is curated for the section.
const IMG = {
  hero: "https://images.unsplash.com/photo-1529070538774-1843cb3265df?auto=format&fit=crop&w=1800&q=80", // crowd / rally
  voters: "https://images.unsplash.com/photo-1494172961521-33799ddd43a5?auto=format&fit=crop&w=1800&q=80", // diverse hands
  community: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1800&q=80", // community meeting
  // Afro Latina portrait represents the multidimensional identity our research emphasizes; Latino is our core specialization and the proving ground for our multilingual playbook.
  language: "https://images.unsplash.com/photo-1714667156704-38240d998c09?auto=format&fit=crop&w=1800&q=80",
  device: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1800&q=80", // phone in hand
};

/* ----------------------------- helpers ------------------------------ */
function Reveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  const { ref, shown } = useReveal<HTMLDivElement>();
  const style: CSSProperties = {
    transitionDelay: `${delay}ms`,
  };
  return (
    <div
      ref={ref}
      style={style}
      className={`transition-all duration-700 ease-out ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
    >
      {children}
    </div>
  );
}

/** A full-bleed parallax band with a stock image that scales up and fades as the user scrolls. */
function ParallaxBand({ src, eyebrow, title, body }: { src: string; eyebrow: string; title: string; body: string }) {
  const { ref, p } = useScrollProgress<HTMLDivElement>();
  // Scale from 1 -> 1.25, fade out near the end so it "disappears" into the next white section.
  const scale = 1 + p * 0.25;
  const translateY = (p - 0.5) * 80; // -40 .. +40 px
  const imgOpacity = p < 0.85 ? 0.55 : Math.max(0, 0.55 - (p - 0.85) * 3);
  const overlay = p < 0.85 ? 0 : Math.min(1, (p - 0.85) * 6);

  return (
    <section ref={ref} className="relative h-[90vh] min-h-[520px] w-full overflow-hidden bg-black">
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url(${src})`,
          transform: `scale(${scale}) translate3d(0, ${translateY}px, 0)`,
          opacity: imgOpacity,
          transition: "opacity 120ms linear",
        }}
      />
      {/* Color wash from the palette */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-brand-500/40 via-black/30 to-accent-400/20" />
      {/* White curtain that closes at the bottom to dissolve into next section */}
      <div aria-hidden className="absolute inset-0 bg-white pointer-events-none" style={{ opacity: overlay }} />
      <div className="relative z-10 mx-auto flex h-full max-w-6xl items-end px-6 pb-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
            {eyebrow}
          </div>
          <h2 className="mt-4 text-3xl font-bold leading-tight text-white drop-shadow-sm sm:text-5xl">{title}</h2>
          <p className="mt-3 max-w-2xl text-base text-white/90 sm:text-lg">{body}</p>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- page ------------------------------ */
export function Marketing() {
  const t = useT();
  const lang = useLang();
  const m = t.marketing;
  const setLanguage = useStore((s) => s.setLanguage);

  // Track scroll for the hero's tiny parallax flourish
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-full bg-white text-ink-900 selection:bg-accent-300 selection:text-ink-900">
      {/* ====================== Nav ====================== */}
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <a href="#top" className="flex items-center gap-2">
            <div className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-lg bg-ink-900">
              <PulseGlyph />
              <span aria-hidden className="absolute -right-2 -top-2 h-4 w-4 rounded-full bg-accent-400" />
              <span aria-hidden className="absolute -bottom-2 -left-2 h-4 w-4 rounded-full bg-brand-400" />
            </div>
            <div className="leading-tight">
              <div className="font-semibold">{t.appName}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{m.hero.eyebrow}</div>
            </div>
          </a>
          <nav className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex">
            <a href="#about" className="hover:text-ink-900">{m.nav.about}</a>
            <a href="#polls" className="hover:text-ink-900">{m.nav.polls}</a>
            <a href="#method" className="hover:text-ink-900">{m.nav.method}</a>
            <Link to="/blog" className="hover:text-ink-900">Blog</Link>
            <a href="#research" className="hover:text-ink-900">{m.nav.research}</a>
            <a href="#contact" className="hover:text-ink-900">{m.nav.contact}</a>
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-1 sm:flex">
              <button
                onClick={() => setLanguage("en")}
                className={`rounded px-2 py-1 text-xs font-semibold transition ${lang === "en" ? "bg-ink-900 text-white" : "text-slate-600 hover:text-ink-900"}`}
              >EN</button>
              <button
                onClick={() => setLanguage("es")}
                className={`rounded px-2 py-1 text-xs font-semibold transition ${lang === "es" ? "bg-ink-900 text-white" : "text-slate-600 hover:text-ink-900"}`}
              >ES</button>
            </div>
            <Link
              to="/app/dashboard"
              className="group inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700"
            >
              {m.nav.launchDemo}
              <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
            </Link>
          </div>
        </div>
      </header>

      {/* ====================== Hero ====================== */}
      <section id="top" className="relative isolate overflow-hidden">
        {/* Decorative shapes echoing the reference palette */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-24 h-[520px] w-[520px] rounded-full bg-brand-300/30 blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.15}px)` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 top-40 h-72 w-72 rounded-full bg-accent-300/40 blur-2xl"
          style={{ transform: `translateY(${scrollY * -0.1}px)` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute right-10 top-32 hidden h-24 w-24 rotate-12 rounded-3xl border-2 border-ink-900/80 md:block"
          style={{ transform: `rotate(${12 + scrollY * 0.05}deg) translateY(${scrollY * 0.05}px)` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-20 left-1/3 hidden h-6 w-6 rounded-full bg-accent-400 md:block"
          style={{ transform: `translateY(${scrollY * -0.08}px)` }}
        />

        <div className="mx-auto grid max-w-6xl gap-12 px-6 py-24 lg:grid-cols-[1.1fr_0.9fr] lg:py-32">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-ink-900/10 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-ink-900 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
              {m.hero.eyebrow}
            </div>
            <h1 className="mt-6 text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
              {m.hero.title.split(" ").slice(0, -2).join(" ")}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{m.hero.title.split(" ").slice(-2).join(" ")}</span>
                <span aria-hidden className="absolute inset-x-0 bottom-1 z-0 h-3 bg-accent-300" />
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate-700">{m.hero.subtitle}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/app/dashboard" className="group inline-flex items-center gap-2 rounded-full bg-ink-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-700">
                {m.hero.ctaPrimary}
                <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
              </Link>
              <a href="#polls" className="inline-flex items-center gap-2 rounded-full border border-ink-900/15 bg-white px-5 py-3 text-sm font-semibold text-ink-900 hover:border-ink-900/40">
                {m.hero.ctaSecondary}
              </a>
            </div>
            <p className="mt-4 text-xs text-slate-500">{m.hero.demoNote}</p>
          </div>

          {/* Decorative phone tile */}
          <div className="relative hidden lg:block">
            <div className="absolute inset-0 -z-10 rotate-3 rounded-3xl bg-brand-400" />
            <div className="absolute inset-0 -z-10 -rotate-2 translate-x-6 translate-y-6 rounded-3xl bg-accent-300" />
            <div className="relative rounded-3xl border border-ink-900/10 bg-white p-5 shadow-xl">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                <div className="h-2 w-2 rounded-full bg-rose-400" />
                <div className="h-2 w-2 rounded-full bg-accent-400" />
                <div className="h-2 w-2 rounded-full bg-brand-400" />
                <div className="ml-auto text-[10px] font-semibold uppercase tracking-widest text-slate-400">SMS · Live</div>
              </div>
              <div className="space-y-3 py-4">
                <Bubble side="them" tone="brand">
                  {lang === "es"
                    ? "Hola, soy María de Pulso Reach. ¿Tienes 60 segundos para una pregunta sobre tu comunidad?"
                    : "Hi, this is María from Pulso Reach. Got 60 seconds for one question about your community?"}
                </Bubble>
                <Bubble side="me">
                  {lang === "es" ? "Claro" : "Sure"}
                </Bubble>
                <Bubble side="them" tone="brand">
                  {lang === "es"
                    ? "¿Qué tema es más importante para ti este año: economía, salud, educación o inmigración?"
                    : "Which issue matters most to you this year: economy, health, education, or immigration?"}
                </Bubble>
                <Bubble side="me">
                  {lang === "es" ? "Salud" : "Health"}
                </Bubble>
                <Bubble side="them" tone="accent">
                  {lang === "es" ? "Gracias. Responde STOP para cancelar." : "Thank you. Reply STOP to opt out."}
                </Bubble>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-[10px] uppercase tracking-widest text-slate-500">
                <span>EN · ES</span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Compliance OK
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== Logo marquee / trust strip ====================== */}
      <div className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl overflow-hidden px-6 py-6">
          <div className="flex items-center gap-10 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            <span className="shrink-0">{lang === "es" ? "Diseñado en torno a" : "Designed around"}</span>
            <div className="flex flex-1 gap-10 overflow-hidden">
              <div className="flex animate-marquee gap-10 whitespace-nowrap">
                {[
                  "TCPA · 10DLC",
                  "STOP / HELP / START",
                  "BISG · GIS",
                  "Quiet hours",
                  "Promotoras",
                  "Pláticas",
                  "Cognitive interviewing",
                  "Mixed-mode",
                  "TCPA · 10DLC",
                  "STOP / HELP / START",
                  "BISG · GIS",
                  "Quiet hours",
                  "Promotoras",
                  "Pláticas",
                  "Cognitive interviewing",
                  "Mixed-mode",
                ].map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-brand-500" /> {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ====================== Parallax band 1 ====================== */}
      <ParallaxBand
        src={IMG.hero}
        eyebrow={lang === "es" ? "62.5M voces" : "62.5M voices"}
        title={m.whyDifferent.title}
        body={m.whyDifferent.body}
      />

      {/* ====================== Stats tiles ====================== */}
      <section id="about" className="relative bg-white">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <h2 className="max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">{m.stats.title}</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {m.stats.items.map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="group relative h-full overflow-hidden rounded-2xl border border-ink-900/10 bg-white p-6 transition hover:-translate-y-1 hover:shadow-xl">
                  <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-brand-100 transition group-hover:scale-150" />
                  <div className="absolute right-3 top-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="relative z-10 text-4xl font-bold text-ink-900">{s.value}</div>
                  <div className="relative z-10 mt-3 text-sm leading-snug text-slate-600">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== Parallax band 2 ====================== */}
      <ParallaxBand
        src={IMG.language}
        eyebrow={lang === "es" ? "Identidad multidimensional" : "Multi-dimensional identity"}
        title={lang === "es"
          ? "Ninguna comunidad es una sola cosa."
          : "No community is one thing."}
        body={lang === "es"
          ? "Afro-latinos, indígenas, mestizos, blancos, bilingües, hispanohablantes, anglohablantes (tres generaciones y veinte países de origen); coreanos, vietnamitas, árabes, haitianos, etíopes (cada uno con sus propias capas). Capturamos país, raza, generación e idioma como campos separados, no como una sola casilla."
          : "Afro-Latino, Indigenous, mestizo, white, bilingual, Spanish-dominant, English-dominant (three generations and twenty countries of origin); Korean, Vietnamese, Arab, Haitian, Ethiopian (each with their own layers). We capture country, race, generation, and language as separate fields, not one checkbox."}
      />

      {/* ====================== Poll types ====================== */}
      <section id="polls" className="relative bg-white">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <div className="flex items-end justify-between gap-6">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-ink-900/10 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-ink-900">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-400" /> {m.nav.polls}
                </div>
                <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{m.pollsHeading}</h2>
                <p className="mt-3 max-w-3xl text-slate-700">{m.pollsSub}</p>
              </div>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {m.polls.map((p, i) => {
              const accents = ["bg-brand-400", "bg-accent-400", "bg-ink-900"] as const;
              const tone = accents[i % accents.length];
              return (
                <Reveal key={i} delay={(i % 3) * 90}>
                  <div className="group relative h-full overflow-hidden rounded-2xl border border-ink-900/10 bg-white p-6 transition hover:-translate-y-1 hover:shadow-xl">
                    <div className={`absolute left-0 top-0 h-1 w-12 ${tone} transition-all duration-500 group-hover:w-full`} />
                    <div className="flex items-center gap-3">
                      <div className="grid h-9 w-9 place-items-center rounded-lg bg-ink-900 text-xs font-bold text-white">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <h3 className="text-base font-semibold">{p.title}</h3>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-slate-600">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====================== Parallax band 3 ====================== */}
      <ParallaxBand
        src={IMG.community}
        eyebrow={lang === "es" ? "Confianza, no pulido" : "Trust, not polish"}
        title={lang === "es"
          ? "Promotoras y aliados comunitarios."
          : "Promotoras and community partners."}
        body={lang === "es"
          ? "Para estudios de salud, cívicos y de poblaciones sensibles, la confianza (no el pulido del canal) es el principal motor de respuesta. Esto vale en cualquier idioma."
          : "For health, civic, and sensitive-population work, trust (not channel polish) is the dominant driver of response. That holds in every language we field."}
      />

      {/* ====================== Methodology ====================== */}
      <section id="method" className="relative bg-white">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <h2 className="max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">{m.method.title}</h2>
            <p className="mt-3 max-w-3xl text-slate-700">{m.method.sub}</p>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {m.method.items.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 100}>
                <div className="group relative flex h-full gap-5 overflow-hidden rounded-2xl border border-ink-900/10 bg-white p-6 transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative shrink-0">
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-ink-900 text-sm font-bold text-white">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div aria-hidden className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-accent-400 transition group-hover:scale-150" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== Parallax band 4 ====================== */}
      <ParallaxBand
        src={IMG.device}
        eyebrow={lang === "es" ? "SMS + WhatsApp" : "SMS + WhatsApp"}
        title={lang === "es"
          ? "Modo mixto por diseño."
          : "Mixed-mode by design."}
        body={lang === "es"
          ? "SMS primero, con WhatsApp, correo, teléfono y en persona como respaldo. El modo es una covariable, no una constante."
          : "SMS first, with WhatsApp, mail, phone, and in-person fallbacks. Mode is a covariate, not a constant."}
      />

      {/* ====================== Demo CTA ====================== */}
      <section className="relative bg-white">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-ink-900 px-8 py-14 text-white sm:px-14">
              <div aria-hidden className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-brand-500/40 blur-3xl" />
              <div aria-hidden className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-accent-400/30 blur-3xl" />
              <div aria-hidden className="absolute right-10 top-10 h-8 w-8 rounded-full bg-accent-400" />
              <div aria-hidden className="absolute bottom-10 right-1/3 h-3 w-24 rounded-full bg-brand-400/80" />

              <div className="relative grid gap-10 md:grid-cols-2">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white/90 backdrop-blur">
                    <span className="h-1.5 w-1.5 rounded-full bg-accent-400" /> Live demo
                  </div>
                  <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{m.demoBlock.title}</h2>
                  <p className="mt-4 max-w-md text-white/80">{m.demoBlock.body}</p>
                  <Link
                    to="/app/dashboard"
                    className="group mt-7 inline-flex items-center gap-2 rounded-full bg-accent-400 px-5 py-3 text-sm font-semibold text-ink-900 transition hover:bg-accent-300"
                  >
                    {m.demoBlock.cta}
                    <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
                  </Link>
                </div>
                <ul className="grid gap-3">
                  {m.demoBlock.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-sm backdrop-blur">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent-400 text-[10px] font-bold text-ink-900">✓</span>
                      <span className="text-white/90">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ====================== Research ====================== */}
      <section id="research" className="relative bg-white">
        <div className="mx-auto max-w-4xl px-6 py-24">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{m.research.title}</h2>
            <p className="mt-3 text-slate-700">{m.research.sub}</p>
          </Reveal>
          <ol className="mt-10 space-y-3 text-sm">
            {m.research.items.map((r, i) => (
              <Reveal key={i} delay={i * 60}>
                <li className="rounded-xl border border-ink-900/10 bg-white p-5 transition hover:border-brand-400">
                  <span className="text-slate-700">{r.cite}</span>{" "}
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="break-all text-brand-700 underline-offset-4 hover:underline">
                    {r.url}
                  </a>
                </li>
              </Reveal>
            ))}
          </ol>
          <p className="mt-6 text-xs italic text-slate-500">{m.research.disclaimer}</p>
        </div>
      </section>

      {/* ====================== Contact ====================== */}
      <section id="contact" className="relative bg-white">
        <div className="mx-auto max-w-4xl px-6 pb-28">
          <Reveal>
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{m.contact.title}</h2>
              <p className="mx-auto mt-3 max-w-xl text-slate-700">{m.contact.body}</p>
            </div>
            <ContactForm />
          </Reveal>
        </div>
      </section>

      {/* ====================== Footer ====================== */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 text-xs text-slate-500 sm:flex-row">
          <div>© {new Date().getFullYear()} Pulso Reach · {m.footer.rights}</div>
          <a href="https://github.com/Mdr-palacios/mensajeando" target="_blank" rel="noopener noreferrer" className="hover:text-ink-900">
            {m.footer.sourceCode} ↗
          </a>
        </div>
      </footer>
    </div>
  );
}

/* ----------------------------- bits ------------------------------ */
function Bubble({ side, tone = "default", children }: { side: "me" | "them"; tone?: "default" | "brand" | "accent"; children: ReactNode }) {
  const base =
    side === "me"
      ? "ml-auto bg-ink-900 text-white"
      : tone === "brand"
        ? "bg-brand-50 text-ink-900 border border-brand-200"
        : tone === "accent"
          ? "bg-accent-100 text-ink-900 border border-accent-200"
          : "bg-slate-100 text-ink-900";
  return (
    <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-snug ${base}`}>
      {children}
    </div>
  );
}
